import { EmployeePosition } from "../api/PartnerPortalGatewayApi";

/**
 * Normalised (lowercase, trimmed) role names that belong to the Leadership group.
 * Order within this array defines sort priority (index 0 = highest priority).
 */
export const LEADERSHIP_ROLES = [
  "service director",
  "service leader",
  "assistant service leader",
  "coordinator",
] as const;

/**
 * Sort priority for leadership roles. Lower number = higher priority (sorted first).
 */
export const LEADERSHIP_SORT_ORDER: Record<string, number> = {
  "service director": 0,
  "service leader": 1,
  "assistant service leader": 2,
  coordinator: 3,
};

/**
 * Sort priority for support roles. Lower number = higher priority (sorted first).
 * Unlisted roles default to 3 (i.e. sorted after the three known support roles).
 */
export const SUPPORT_SORT_ORDER: Record<string, number> = {
  educator: 0,
  "inclusion support": 1,
  overstaff: 2,
};

const UNKNOWN_SUPPORT_PRIORITY = 3;

/**
 * Normalises a position name to a consistent, comparable string.
 * Returns an empty string when the value is absent.
 */
const normaliseRole = (positionName: string | undefined): string => (positionName ?? "").trim().toLowerCase();

/**
 * Returns true when the given positionName maps to a Leadership role.
 */
export const isLeadershipRole = (positionName: string | undefined): boolean =>
  (LEADERSHIP_ROLES as readonly string[]).includes(normaliseRole(positionName));

/**
 * Returns the sort priority for a leadership role.
 * Known leadership roles receive a priority of 0–3; anything else gets MAX_SAFE_INTEGER
 * so it would fall to the end (should never be reached because support roles never enter
 * the leadership array).
 */
const leadershipPriority = (positionName: string | undefined): number =>
  LEADERSHIP_SORT_ORDER[normaliseRole(positionName)] ?? Number.MAX_SAFE_INTEGER;

/**
 * Returns the sort priority for a support role.
 * Known support roles receive 0–2; everything else receives UNKNOWN_SUPPORT_PRIORITY (3).
 */
const supportPriority = (positionName: string | undefined): number =>
  SUPPORT_SORT_ORDER[normaliseRole(positionName)] ?? UNKNOWN_SUPPORT_PRIORITY;

/**
 * Splits an array of EmployeePosition records into Leadership and Educators & Support
 * groups, sorting each group by its configured role priority.
 *
 * - Leadership: Service Director → Service Leader → Assistant Service Leader → Coordinator
 * - Support:    Educator → Inclusion Support → Overstaff → (other/unknown roles)
 *
 * The original array is not mutated.
 */
export interface EducatorGroups {
  leadership: EmployeePosition[];
  support: EmployeePosition[];
}

export const groupAndSortEducators = (educators: EmployeePosition[]): EducatorGroups => {
  const leadership: EmployeePosition[] = [];
  const support: EmployeePosition[] = [];

  for (const educator of educators) {
    if (isLeadershipRole(educator.positionName)) {
      leadership.push(educator);
    } else {
      support.push(educator);
    }
  }

  leadership.sort((a, b) => leadershipPriority(a.positionName) - leadershipPriority(b.positionName));

  support.sort((a, b) => supportPriority(a.positionName) - supportPriority(b.positionName));

  return { leadership, support };
};
